import {CommonModule} from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeConfirmDialogComponent} from '../../gatewee-confirm-dialog/gatewee-confirm-dialog.component';
import {Integration} from '@gatewee/types/integration';
import {
  CreateIntegrationRoutingRulePayload,
  IntegrationRoutingRule,
  UpdateIntegrationRoutingRulePayload,
} from '@gatewee/types/integration-routing-rule';
import {Wallet, walletProvider} from '@gatewee/types/wallet';
import {UtilsModule} from '@gatewee/utils';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogService, TuiTitle} from '@taiga-ui/core';
import {
  TuiTableDirective,
  TuiTableTbody,
  TuiTableTd,
  TuiTableTh,
} from '@taiga-ui/addon-table';
import {TuiCell} from '@taiga-ui/layout';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {
  IntegrationRoutingRuleFormComponent,
  IntegrationRoutingRuleFormResult,
} from '../integration-routing-rule-form/integration-routing-rule-form.component';

export interface IntegrationRoutingRuleActions {
  fetchRules: (walletId: number) => Observable<IntegrationRoutingRule[]>;
  createRule: (
    payload: CreateIntegrationRoutingRulePayload,
  ) => Observable<IntegrationRoutingRule>;
  updateRule: (
    id: number,
    payload: UpdateIntegrationRoutingRulePayload,
  ) => Observable<IntegrationRoutingRule>;
  deleteRule: (id: number) => Observable<void>;
  fetchRoutableIntegrations: (recipientId: number) => Observable<Integration[]>;
}

@Component({
  selector: 'app-integration-routing-rules',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    UtilsModule,
    GateweeButtonComponent,
    TuiTableDirective,
    TuiTableTh,
    TuiTableTd,
    TuiTableTbody,
    TuiTitle,
    TuiCell,
  ],
  templateUrl: './integration-routing-rules.component.html',
})
export class IntegrationRoutingRulesComponent implements OnInit, OnChanges {
  @Input({required: true}) wallet!: Wallet;
  @Input() actions?: IntegrationRoutingRuleActions;

  rules: IntegrationRoutingRule[] = [];
  routableIntegrations: Integration[] = [];
  loading = false;

  constructor(
    private readonly dialogService: TuiDialogService,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
  ) {}

  get routerEnabled(): boolean {
    return !!this.wallet?.routingEnabled;
  }

  get recipientId(): number | null {
    return this.wallet?.recipient?.id ?? null;
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wallet'] && !changes['wallet'].firstChange) {
      this.loadData();
    }
  }

  loadData(): void {
    this.loadRules();
    this.loadRoutableIntegrations();
  }

  loadRules(): void {
    if (!this.wallet?.id || !this.actions?.fetchRules) {
      this.rules = [];
      return;
    }

    this.loading = true;
    this.actions
      .fetchRules(this.wallet.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: rows => {
          this.rules = rows ?? [];
        },
        error: () => {
          this.rules = [];
          this.alerts
            .open(
              this.translate.instant(
                'features.wallets.integrationRouting.loadError',
              ),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
        },
      });
  }

  private loadRoutableIntegrations(): void {
    const recipientId = this.recipientId;
    if (!recipientId || !this.actions?.fetchRoutableIntegrations) {
      this.routableIntegrations = [];
      return;
    }

    this.actions.fetchRoutableIntegrations(recipientId).subscribe({
      next: integrations => {
        this.routableIntegrations = this.filterRoutableIntegrations(
          integrations ?? [],
        );
      },
      error: () => {
        this.routableIntegrations = [];
      },
    });
  }

  private filterRoutableIntegrations(
    integrations: Integration[],
  ): Integration[] {
    const boundIntegrationId = this.walletBoundIntegrationId();
    const defaultProvider = walletProvider(this.wallet);

    return integrations.filter(integration => {
      if (integration.routable === false) {
        return false;
      }
      if (
        boundIntegrationId !== null &&
        integration.id === boundIntegrationId
      ) {
        return false;
      }
      if (defaultProvider && integration.provider === defaultProvider) {
        return false;
      }
      return true;
    });
  }

  private walletBoundIntegrationId(): number | null {
    return (
      this.wallet.integration?.id ?? this.wallet.pool?.integration?.id ?? null
    );
  }

  formatTargetIntegration(rule: IntegrationRoutingRule): string {
    const integration = rule.targetIntegration;
    const providerLabel = this.translate.instant(
      `enums.paymentProvider.${integration.provider}`,
    );
    const suffix = integration.description?.trim()
      ? ` — ${integration.description.trim()}`
      : '';
    return `#${integration.id} · ${providerLabel}${suffix}`;
  }

  formatAmountRange(rule: IntegrationRoutingRule): string {
    const min = this.parseAmount(rule.minAmount);
    const max = this.parseAmount(rule.maxAmount);
    if (min === null && max === null) {
      return this.translate.instant(
        'features.wallets.integrationRouting.anyAmount',
      );
    }
    if (min !== null && max !== null) {
      return `${min} – ${max}`;
    }
    if (min !== null) {
      return this.translate.instant(
        'features.wallets.integrationRouting.minOnly',
        {min},
      );
    }
    return this.translate.instant(
      'features.wallets.integrationRouting.maxOnly',
      {max},
    );
  }

  private parseAmount(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(String(v));
    return Number.isFinite(n) ? n : null;
  }

  openCreateDialog(): void {
    if (!this.actions || !this.recipientId) return;

    this.dialogService
      .open<IntegrationRoutingRuleFormResult | void>(
        new PolymorpheusComponent(IntegrationRoutingRuleFormComponent),
        {
          data: {
            walletId: this.wallet.id,
            routableIntegrations: this.routableIntegrations,
            editRule: null,
          },
          size: 'm',
          label: this.translate.instant(
            'features.wallets.integrationRouting.dialog.createTitle',
          ),
          dismissible: true,
          closeable: true,
        },
      )
      .subscribe(result => this.handleFormResult(result));
  }

  openEditDialog(rule: IntegrationRoutingRule): void {
    if (!this.actions || !this.recipientId) return;

    this.dialogService
      .open<IntegrationRoutingRuleFormResult | void>(
        new PolymorpheusComponent(IntegrationRoutingRuleFormComponent),
        {
          data: {
            walletId: this.wallet.id,
            routableIntegrations: this.routableIntegrations,
            editRule: rule,
          },
          size: 'm',
          label: this.translate.instant(
            'features.wallets.integrationRouting.dialog.editTitle',
          ),
          dismissible: true,
          closeable: true,
        },
      )
      .subscribe(result => this.handleFormResult(result));
  }

  private handleFormResult(
    result: IntegrationRoutingRuleFormResult | void,
  ): void {
    if (!result || !this.actions) return;

    if (result.kind === 'create') {
      this.actions.createRule(result.payload).subscribe({
        next: () => {
          this.toastSuccess('features.wallets.integrationRouting.saved');
          this.loadRules();
        },
        error: () =>
          this.toastError('features.wallets.integrationRouting.saveError'),
      });
      return;
    }

    this.actions.updateRule(result.id, result.payload).subscribe({
      next: () => {
        this.toastSuccess('features.wallets.integrationRouting.saved');
        this.loadRules();
      },
      error: () =>
        this.toastError('features.wallets.integrationRouting.saveError'),
    });
  }

  toggleActive(rule: IntegrationRoutingRule): void {
    if (!this.actions) return;

    this.actions.updateRule(rule.id, {isActive: !rule.isActive}).subscribe({
      next: () => {
        this.toastSuccess('features.wallets.integrationRouting.saved');
        this.loadRules();
      },
      error: () =>
        this.toastError('features.wallets.integrationRouting.saveError'),
    });
  }

  confirmDelete(rule: IntegrationRoutingRule): void {
    if (!this.actions) return;

    this.dialogService
      .open<boolean>(new PolymorpheusComponent(GateweeConfirmDialogComponent), {
        data: {
          title: this.translate.instant(
            'features.wallets.integrationRouting.deleteConfirmTitle',
          ),
          message: this.translate.instant(
            'features.wallets.integrationRouting.deleteConfirmMessage',
          ),
          confirmLabel: this.translate.instant('common.actions.confirm'),
          cancelLabel: this.translate.instant('common.actions.cancel'),
        },
        size: 's',
        dismissible: true,
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.actions!.deleteRule(rule.id).subscribe({
          next: () => {
            this.toastSuccess('features.wallets.integrationRouting.deleted');
            this.loadRules();
          },
          error: () =>
            this.toastError('features.wallets.integrationRouting.deleteError'),
        });
      });
  }

  private toastSuccess(key: string): void {
    this.alerts
      .open(this.translate.instant(key), {
        label: this.translate.instant('common.status.success'),
        appearance: 'success',
      })
      .subscribe();
  }

  private toastError(key: string): void {
    this.alerts
      .open(this.translate.instant(key), {
        label: this.translate.instant('common.status.error'),
        appearance: 'error',
      })
      .subscribe();
  }
}
